"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useStytch } from "@stytch/nextjs";
import { useConvexAuth } from "convex/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { Logo } from "@/components/landing/Logo";
import { useBoard } from "@/components/BoardProvider";
import { captureClientEvent } from "@/lib/analytics/client";
import { safeInternalPath, stashReturnUrl } from "@/lib/authRedirect";
import { DiscordIcon, GoogleIcon } from "@/components/auth/ProviderIcons";

export type AuthMode = "signin" | "signup";

// 30 days — must be <= the project's max_session_duration_minutes.
const SESSION_DURATION_MINUTES = 43200;

export function AuthPage({ mode }: { mode: AuthMode }) {
  const stytch = useStytch();
  const router = useRouter();
  const params = useSearchParams();
  const board = useBoard();
  const { isLoading, isAuthenticated } = useConvexAuth();

  // "/" and the auth pages themselves are never worth returning to: signing in
  // should land you in the app, and a self-referential redirect would loop.
  const asked = safeInternalPath(params.get("redirect"));
  const requested =
    !asked || asked === "/" || asked.startsWith("/login") || asked.startsWith("/signup")
      ? null
      : asked;
  const destination = requested ?? "/dashboard";
  const signup = mode === "signup";

  const [email, setEmail] = useState("");
  const [methodId, setMethodId] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendCount, setResendCount] = useState(0);
  const [cooldown, setCooldown] = useState(30); // seconds until next resend allowed
  const cooldownRef = useRef<NodeJS.Timeout | null>(null);
  const submittingRef = useRef(false);
  const settledRef = useRef(false);

  useEffect(() => {
    captureClientEvent("auth_modal_opened", { mode, surface: "page" });
  }, [mode]);

  // Someone who is already signed in has no business on a sign-in form. Judge
  // once, on the first resolve, so the post-verification navigation below isn't
  // fighting this effect for the same destination.
  useEffect(() => {
    if (isLoading || settledRef.current) return;
    settledRef.current = true;
    if (isAuthenticated) router.replace(destination);
  }, [isLoading, isAuthenticated, destination, router]);

  // Restart the cooldown whenever a code goes out, backing off on each resend.
  useEffect(() => {
    if (!codeSent) return;
    setCooldown(30 * Math.pow(2, resendCount));

    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          if (cooldownRef.current) clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, [codeSent, resendCount]);

  // A first-time signup is pulled into onboarding the moment auth resolves,
  // which can happen before we reach the destination. Leave it where the
  // onboarding flow looks for it so they still end up there.
  const rememberDestination = useCallback(() => {
    if (destination === "/dashboard") return;
    stashReturnUrl(destination);
  }, [destination]);

  const oauthRedirect = (provider: "google" | "discord") => {
    captureClientEvent("auth_social_sign_in", { provider, mode });
    rememberDestination();
    // Stash the post-sign-in destination for the /authenticate callback to read
    // back. It can't ride on the redirect URL as a query param: Stytch validates
    // OAuth redirect URLs against an exact allowlist and rejects extra params.
    try {
      window.sessionStorage.setItem("auth_redirect", destination);
    } catch {}
    const origin = window.location.origin;
    // The board is applied as a session custom claim in /authenticate.
    void stytch.oauth[provider]
      .start({
        login_redirect_url: `${origin}/authenticate`,
        signup_redirect_url: `${origin}/authenticate`,
      })
      .catch(console.error);
  };

  // Stytch loginOrCreate handles both new and existing users in one call.
  const sendCode = async (resend: boolean) => {
    setLoading(true);
    setError(null);
    try {
      captureClientEvent("auth_email_submitted", {
        mode,
        method: "email_otp",
        resend,
        attempt: resendCount + (resend ? 1 : 0),
      });
      // Stytch only applies the branded template when the send references it;
      // both board projects define the same template id.
      const res = await stytch.otps.email.loginOrCreate(email, {
        expiration_minutes: 5,
        login_template_id: "initial_style_template",
        signup_template_id: "initial_style_template",
      });
      setMethodId(res.method_id);
      setCodeSent(true);
      if (resend) setResendCount((c) => c + 1);
      captureClientEvent("auth_otp_sent", { mode, resend });
    } catch (err: any) {
      const message =
        err?.error_message ?? (err as Error)?.message ?? "Failed to send verification code.";
      setError(message);
      captureClientEvent("auth_otp_error", {
        mode,
        stage: resend ? "resend" : "send",
        message,
      });
    } finally {
      setLoading(false);
    }
  };

  const submitCode = useCallback(async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      await stytch.otps.authenticate(code, methodId, {
        session_duration_minutes: SESSION_DURATION_MINUTES,
      });
      captureClientEvent("auth_signed_in", { mode, method: "email_otp" });
      rememberDestination();
      router.replace(destination);
    } catch (err: any) {
      const message = err?.error_message ?? "The verification code is incorrect. Please try again.";
      setError("The verification code is incorrect. Please try again.");
      captureClientEvent("auth_otp_error", { mode, stage: "verify", message });
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  }, [code, methodId, mode, stytch, router, destination, rememberDestination]);

  // Auto-submit once all six digits are in.
  useEffect(() => {
    if (code.length === 6 && codeSent && !loading) void submitCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const swapHref = `${signup ? "/login" : "/signup"}${
    requested ? `?redirect=${encodeURIComponent(requested)}` : ""
  }`;

  const subtitle = requested?.startsWith("/dashboard/resources")
    ? "Create your free account to download the handbook and challenge papers."
    : requested
      ? "Sign in to pick up where you left off."
      : signup
        ? "Your progress saves from the first question."
        : "Sign in to pick up where you left off.";

  return (
    <div className="min-h-[100dvh] bg-sidebar">
      <div className="flex min-h-[100dvh] flex-col lg:flex-row lg:gap-4 lg:p-4">
        <aside className="hidden lg:flex lg:w-[46%] lg:flex-col lg:justify-between lg:py-8 lg:pl-8 lg:pr-4">
          <Logo />

          <div className="max-w-md">
            <p className="font-script text-[2.75rem] leading-[1.08] text-foreground xl:text-5xl">
              Walk into the {board.shortName} having seen it all before.
            </p>
            <p className="mt-6 font-polysans text-base leading-relaxed text-muted-foreground">
              Every past paper worked through, timed mocks in the real exam interface, and
              practice that follows the topics you keep dropping marks on.
            </p>
          </div>

          <p className="font-polysans text-sm text-muted-foreground">Free to start.</p>
        </aside>

        <main className="flex flex-1 flex-col justify-center bg-background px-6 py-10 sm:px-10 lg:rounded-3xl lg:border lg:border-border lg:px-16 lg:py-12">
          <div className="flex justify-center lg:hidden">
            <Logo />
          </div>

          <div className="mx-auto my-auto w-full max-w-sm">
            {!codeSent ? (
              <>
                <h1 className="font-script text-3xl leading-tight text-foreground sm:text-4xl">
                  {signup ? "Create your account" : "Welcome back"}
                </h1>
                <p className="mt-2 font-polysans text-sm text-muted-foreground">{subtitle}</p>

                <div className="mt-8 grid grid-cols-2 gap-3">
                  <ProviderTile
                    label="Google"
                    icon={<GoogleIcon className="h-6 w-6" />}
                    onClick={() => oauthRedirect("google")}
                  />
                  <ProviderTile
                    label="Discord"
                    icon={<DiscordIcon className="h-6 w-6" />}
                    onClick={() => oauthRedirect("discord")}
                  />
                </div>

                <div className="my-6 flex items-center gap-3">
                  <span className="h-px flex-1 bg-border" />
                  <span className="font-polysans text-xs uppercase tracking-widest text-muted-foreground">
                    or
                  </span>
                  <span className="h-px flex-1 bg-border" />
                </div>

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    void sendCode(false);
                  }}
                >
                  <label
                    htmlFor="auth-email"
                    className="mb-1.5 block font-polysans text-sm font-medium text-foreground"
                  >
                    Email
                  </label>
                  <Input
                    id="auth-email"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="you@school.ac.uk"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    size="lg"
                  />
                  <Button type="submit" size="lg" className="mt-4 w-full" loading={loading}>
                    Continue
                  </Button>
                </form>

                {error && <p className="mt-3 text-sm text-destructive">{error}</p>}

                <p className="mt-8 text-center font-polysans text-sm text-muted-foreground">
                  {signup ? "Already have an account? " : "New here? "}
                  <Link
                    href={swapHref}
                    className="font-medium text-primary underline-offset-4 hover:underline"
                  >
                    {signup ? "Log in" : "Create an account"}
                  </Link>
                </p>
              </>
            ) : (
              <div className="flex flex-col items-center">
                <h1 className="font-script text-3xl leading-tight text-foreground sm:text-4xl">
                  Check your email
                </h1>
                <p className="mt-2 text-center font-polysans text-sm text-muted-foreground">
                  We sent a 6-digit code to <span className="break-all font-medium">{email}</span>
                </p>

                <InputOTP
                  value={code}
                  onChange={setCode}
                  maxLength={6}
                  autoFocus
                  containerClassName="mt-8"
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} className="h-11 w-10 text-base" />
                    <InputOTPSlot index={1} className="h-11 w-10 text-base" />
                    <InputOTPSlot index={2} className="h-11 w-10 text-base" />
                  </InputOTPGroup>
                  <InputOTPSeparator className="mx-1 text-muted-foreground" />
                  <InputOTPGroup>
                    <InputOTPSlot index={3} className="h-11 w-10 text-base" />
                    <InputOTPSlot index={4} className="h-11 w-10 text-base" />
                    <InputOTPSlot index={5} className="h-11 w-10 text-base" />
                  </InputOTPGroup>
                </InputOTP>

                {error && <p className="mt-4 text-sm text-destructive">{error}</p>}

                <Button
                  size="lg"
                  className="mt-8 w-full"
                  loading={loading}
                  onClick={() => void submitCode()}
                >
                  Verify and continue
                </Button>

                <p className="mt-6 text-center font-polysans text-sm text-muted-foreground">
                  It can take a minute, and it sometimes lands in spam.
                </p>
                <Button
                  variant="link"
                  className="mt-1 h-auto p-0 text-primary disabled:text-muted-foreground"
                  onClick={() => void sendCode(true)}
                  disabled={cooldown > 0 || loading}
                >
                  {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
                </Button>

                <Button
                  variant="ghost"
                  className="mt-4 w-full text-muted-foreground"
                  onClick={() => {
                    setCodeSent(false);
                    setCode("");
                    setError(null);
                  }}
                >
                  Use a different email
                </Button>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

function ProviderTile({
  label,
  icon,
  onClick,
}: {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center gap-2 rounded-xl border border-border bg-card px-4 py-5 font-polysans text-sm font-medium text-foreground transition-colors duration-200 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-safe:active:scale-[0.98]"
    >
      {icon}
      {label}
    </button>
  );
}
